// Libs
import { Box, Stack, Typography } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import classNames from 'classnames/bind';
import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
// Components, Layouts, Pages
// Others
import { configLineChart } from '~/utils/constants/chart';
import { DEFAULT_NUMBER_ZERO } from '~/utils/constants/common';
import { IBaseChartData } from '~/utils/interface/common';
// Styles, images, icons
import styles from './LineChart.module.scss';

type Props = {
  title?: string;
  subtitle?: string;
  data: IBaseChartData;
  height?: number | string;
  width?: number | string;
  colors?: string[];
  categories: string[];
  yAxisFormatter?: (value: number) => string;
  tooltipFormatter?: (value: number) => string;
  customOptions?: ApexOptions;
};

const cx = classNames.bind(styles);

const DEFAULT_CUSTOM_OPTIONS: ApexOptions = {};

const LineChart = (props: Props) => {
  //#region Destructuring Props
  const {
    title,
    subtitle,
    data,
    height = 400,
    width = '100%',
    colors = [],
    categories = [],
    yAxisFormatter,
    tooltipFormatter,
    customOptions = DEFAULT_CUSTOM_OPTIONS,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [configChart, setConfigChart] = useState<ApexOptions | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  const formatSubtitle = useMemo(() => {
    if (subtitle && data?.total) {
      return `${subtitle} ${yAxisFormatter ? yAxisFormatter(data.total) : data.total || DEFAULT_NUMBER_ZERO}`;
    }

    if (subtitle) {
      return subtitle;
    }
  }, [subtitle, data?.total, yAxisFormatter]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!data?.chart) return;

    // --- Base config ---
    const baseConfig: ApexOptions = configLineChart();

    // --- Helpers ---
    const safeYAxis = !Array.isArray(customOptions.yaxis) ? customOptions.yaxis : undefined;
    const safeXAxis = customOptions.xaxis;
    const safeTooltipY = !Array.isArray(customOptions.tooltip?.y) ? customOptions.tooltip?.y : undefined;

    const finalYAxisLabels = {
      ...(safeYAxis?.labels || {}),
      formatter: yAxisFormatter || safeYAxis?.labels?.formatter,
    };

    const finalTooltipY = {
      ...(safeTooltipY || {}),
      formatter: tooltipFormatter || safeTooltipY?.formatter,
    };

    // --- Merged config ---
    const mergedConfig: ApexOptions = {
      ...baseConfig,
      ...customOptions,
      series: data.chart,
      colors: colors?.length > 0 ? colors : customOptions.colors,
      xaxis: {
        ...baseConfig.xaxis,
        ...(safeXAxis || {}),
        categories: categories ?? safeXAxis?.categories,
      },
      yaxis: {
        ...baseConfig.yaxis,
        ...(safeYAxis || {}),
        labels: finalYAxisLabels,
      },
      tooltip: {
        ...baseConfig.tooltip,
        ...(customOptions.tooltip || {}),
        y: finalTooltipY,
      },
    };

    setConfigChart({ ...mergedConfig });
  }, [data?.chart, categories, colors, customOptions, tooltipFormatter, yAxisFormatter]);
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Stack
      component={'div'}
      id='lineChartComponent'
      direction={'column'}
      spacing={2}
      width={width}
      className={cx('container')}
    >
      {title && (
        <Box className={cx('header')}>
          <Typography component='p' className={cx('title')}>
            {title}
          </Typography>
        </Box>
      )}

      <Stack direction={'column'} spacing={1} className={cx('chartBody')}>
        {formatSubtitle && (
          <Box component={'div'}>
            <Typography component='p' className={cx('subtitle')}>
              {formatSubtitle}
            </Typography>
          </Box>
        )}

        {configChart && (
          <ReactApexChart
            type='line'
            width={'100%'}
            height={height}
            options={configChart}
            series={configChart.series}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default LineChart;
